(() => {
  const SELECTOR = '[data-delivery-timeframe]';
  const DAY_MS = 24 * 60 * 60 * 1000;

  function getTimezoneParts(timeZone) {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const parts = {};

    formatter.formatToParts(new Date()).forEach((part) => {
      if (part.type !== 'literal') parts[part.type] = part.value;
    });

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
    };
  }

  function createLogicalDate(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day, 12));
  }

  function addCalendarDays(date, amount) {
    return new Date(date.getTime() + amount * DAY_MS);
  }

  function getMonthDay(date) {
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${month}-${day}`;
  }

  function isIncludedDate(date, includedWeekdays, blackoutDates) {
    return includedWeekdays.has(date.getUTCDay()) && !blackoutDates.has(getMonthDay(date));
  }

  function calculateDeliveryDate({ baseDate, numberOfDays, startingOffset, includedWeekdays, blackoutDates }) {
    let currentDate = addCalendarDays(baseDate, startingOffset);
    let countedDays = 0;

    for (let safetyCounter = 0; safetyCounter < 730; safetyCounter += 1) {
      if (isIncludedDate(currentDate, includedWeekdays, blackoutDates)) {
        countedDays += 1;
        if (countedDays >= numberOfDays) return currentDate;
      }
      currentDate = addCalendarDays(currentDate, 1);
    }

    throw new Error('Unable to calculate delivery date.');
  }

  function formatDateRange(fromDate, toDate, locale) {
    const formatter = new Intl.DateTimeFormat(locale || 'en', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });

    if (fromDate.getTime() === toDate.getTime()) return formatter.format(fromDate);
    if (typeof formatter.formatRange === 'function') return formatter.formatRange(fromDate, toDate);
    return `${formatter.format(fromDate)} – ${formatter.format(toDate)}`;
  }

  function parseISODate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (!match) return null;

    const date = createLogicalDate(Number(match[1]), Number(match[2]), Number(match[3]));
    if (
      date.getUTCFullYear() !== Number(match[1]) ||
      date.getUTCMonth() + 1 !== Number(match[2]) ||
      date.getUTCDate() !== Number(match[3])
    ) {
      return null;
    }

    return date;
  }

  function formatSingleDate(date, locale) {
    return new Intl.DateTimeFormat(locale || 'en', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(date);
  }

  function setElementValue(element, value) {
    if (element instanceof HTMLInputElement) {
      element.value = value;
    } else if (element.textContent.trim() !== value) {
      element.textContent = value;
    }
  }

  function renderElement(element) {
    try {
      const kind = element.dataset.deliveryKind || 'range';
      const locale = element.dataset.locale || document.documentElement.lang || 'en';
      const prefix = element.dataset.prefix || (kind === 'arrival' ? 'Available from' : 'Estimated delivery:');

      if (kind === 'arrival') {
        const arrivalDate = parseISODate(element.dataset.arrivalDate);
        if (!arrivalDate) return;

        setElementValue(element, `${prefix} ${formatSingleDate(arrivalDate, locale)}`);
        return;
      }

      const minDays = Number(element.dataset.minDays);
      const maxDays = Number(element.dataset.maxDays);
      if (!Number.isFinite(minDays) || !Number.isFinite(maxDays) || minDays <= 0 || maxDays <= 0) return;

      const timeZone = element.dataset.timezone || 'Europe/Amsterdam';
      const cutoffHour = Number(element.dataset.cutoffHour || 15);
      const includedWeekdays = new Set(
        String(element.dataset.includedWeekdays || '1,2,3,4,5,6')
          .split(',')
          .map(Number)
          .filter(Number.isFinite)
      );
      const blackoutDates = new Set(
        String(element.dataset.blackoutDates || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      );
      const timezoneParts = getTimezoneParts(timeZone);
      const baseDate = createLogicalDate(timezoneParts.year, timezoneParts.month, timezoneParts.day);
      const startingOffset = timezoneParts.hour >= cutoffHour ? 2 : 1;
      const calculation = { baseDate, startingOffset, includedWeekdays, blackoutDates };
      const earliestDate = calculateDeliveryDate({ ...calculation, numberOfDays: minDays });
      const latestDate = calculateDeliveryDate({ ...calculation, numberOfDays: maxDays });

      setElementValue(element, `${prefix} ${formatDateRange(earliestDate, latestDate, locale)}`);
    } catch (error) {
      console.error('Delivery timeframe error:', error);
    }
  }

  function renderAll(root = document) {
    if (root.matches?.(SELECTOR)) renderElement(root);
    root.querySelectorAll?.(SELECTOR).forEach(renderElement);
  }

  function initialize() {
    renderAll();

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) renderAll(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => renderAll(event.target));
})();
